import { useSeo } from "../lib/useSeo";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { ASSETS, AREAS_DC, AREAS_MD, AREA_GUIDES } from "../lib/constants";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, Label, MapBlock } from "../components/bits";

const Chip = ({ children }) => (
  <span className="border border-white/10 bg-ink-2 px-4 py-2 text-sm text-white/70 transition-colors duration-300 hover:border-gold/50 hover:text-white">
    {children}
  </span>
);

export default function AreasServed() {
  useSeo("/areas-served");
  return (
    <div data-testid="areas-page">
      <PageHero
        label="Serving DC & Maryland"
        title="Your Local Washington DC Cannabis Dispensary"
        subtitle="Higher Ground DC proudly serves Washington, DC and nearby Maryland communities with premium products and exceptional service."
        image={ASSETS.exteriorMural}
        testid="areas-hero"
      />

      {/* Neighborhood lists */}
      <section className="bg-ink py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>Washington, DC Areas Served</Label></Reveal>
          <Stagger className="mt-8 flex flex-wrap gap-3" delayChildren={0.01}>
            {AREAS_DC.map((a) => (
              <StaggerItem key={a}><Chip>{a}</Chip></StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-16"><Label>Nearby Maryland Areas Served</Label></Reveal>
          <Stagger className="mt-8 flex flex-wrap gap-3" delayChildren={0.01}>
            {AREAS_MD.map((a) => (
              <StaggerItem key={a}><Chip>{a}</Chip></StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Location guides (SEO) */}
      <section className="border-t border-white/10 bg-ink-2 py-16 md:py-24" data-testid="area-guides">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>Find a Dispensary Near You</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
              Neighborhood Location Guides
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {AREA_GUIDES.map((g) => (
              <StaggerItem key={g.slug}>
                <Link
                  to={`/dispensary/${g.slug}`}
                  data-testid={`area-guide-${g.slug}`}
                  className="group flex h-full flex-col bg-ink p-8 transition-colors duration-500 hover:bg-ink-3"
                >
                  <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
                    <MapPin className="h-3.5 w-3.5" /> {g.region}
                  </span>
                  <h3 className="mt-3 font-display text-xl font-light uppercase tracking-[0.08em] text-white transition-colors group-hover:text-gold">
                    Near {g.name}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">{g.blurb}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-all group-hover:gap-3 group-hover:text-gold">
                    View Guide <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <MapBlock label="Find Us on the Map" />
    </div>
  );
}
